import React, { createContext, useContext, useState, useEffect } from 'react';
import { getFirestore, collection, getDocs, writeBatch, doc, increment, getDoc, query, where, orderBy } from 'firebase/firestore';
import { useFirebase } from './FirebaseContext';
import { formatDate } from '../hooks/DateTime';
import { ProviderData, EventData, BlogData, FollowRelation } from '../types/firebase';



interface FirebaseData {
  eventsData: EventData[];
  providerData: ProviderData[];
  blogsData: BlogData[];
  loading: boolean;
  error: Error | null;
  createProviderWithProfile: (userId: string, providerData: ProviderData) => Promise<void>;
  followPractitioner: (practitionerId: string, followerId: string) => Promise<void>;
  unfollowPractitioner: (practitionerId: string, followerId: string) => Promise<void>;
  isFollowing: (practitionerId: string, followerId: string) => Promise<boolean>;
  getUserFollowing: (userId: string) => Promise<FollowRelation[]>;
}

const DataContext = createContext<FirebaseData | null>(null);

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [data, setData] = useState<Omit<FirebaseData, 'createProviderWithProfile' | 'followPractitioner' | 'unfollowPractitioner' | 'isFollowing' | 'getUserFollowing'>>({
    eventsData: [],
    providerData: [],
    blogsData: [],
    loading: true,
    error: null
  });
  
  const {app} = useFirebase();

  const createProviderWithProfile = async (userId: string, providerData: ProviderData) => {
    const db = getFirestore(app);
    const batch = writeBatch(db);

    // Create provider document
    const providerRef = doc(db, 'providers', providerData.id);
    batch.set(providerRef, providerData);

    // Update user's profile with provider logo
    const userRef = doc(db, 'users', userId);
    batch.update(userRef, {
      profile_url: providerData.provider_logo,
      role: 'provider'
    });

    await batch.commit();

    // Update local state
    setData(prev => ({
      ...prev,
      providerData: [...prev.providerData, providerData]
    }));
  };

  const followPractitioner = async (practitionerId: string, followerId: string) => {
    const db = getFirestore(app);
    const batch = writeBatch(db);

    try {
      // Get practitioner data first
      const practitionerRef = doc(db, 'providers', practitionerId);
      const practitionerDoc = await getDoc(practitionerRef);
      const practitionerData = practitionerDoc.data() as ProviderData;

      // Create the follow relation document with denormalized data
      const followId = `${followerId}_${practitionerId}`;
      const followRef = doc(db, 'follows', followId);
      const followData: FollowRelation = {
        follower_id: followerId,
        following_id: practitionerId,
        created_at: new Date().toISOString(),
        following_name: practitionerData.provider_name,
        following_logo: practitionerData.provider_logo || '',
        following_types: practitionerData.provider_types
      };
      batch.set(followRef, followData);

      // Update provider's followers count
      const providerRef = doc(db, 'providers', practitionerId);
      batch.update(providerRef, {
        followers_count: increment(1)
      });

      // Update user's following count
      const userRef = doc(db, 'users', followerId);
      batch.update(userRef, {
        following_count: increment(1)
      });

      await batch.commit();

      // Update local state
      setData(prev => ({
        ...prev,
        providerData: prev.providerData.map(provider => 
          provider.id === practitionerId 
            ? { ...provider, followers_count: (provider.followers_count || 0) + 1 }
            : provider
        )
      }));

    } catch (error) {
      throw error;
    }
  };

  const unfollowPractitioner = async (practitionerId: string, followerId: string) => {
    const db = getFirestore(app);
    const batch = writeBatch(db);

    try {
      // Delete the follow relation document
      const followId = `${followerId}_${practitionerId}`;
      const followRef = doc(db, 'follows', followId);
      batch.delete(followRef);

      // Update provider's followers count
      const providerRef = doc(db, 'providers', practitionerId);
      batch.update(providerRef, {
        followers_count: increment(-1)
      });

      // Update user's following count
      const userRef = doc(db, 'users', followerId);
      batch.update(userRef, {
        following_count: increment(-1)
      });

      await batch.commit();

      // Update local state
      setData(prev => ({
        ...prev,
        providerData: prev.providerData.map(provider => 
          provider.id === practitionerId 
            ? { ...provider, followers_count: Math.max((provider.followers_count || 0) - 1, 0) }
            : provider
        )
      }));

    } catch (error) {
      throw error;
    }
  };

  const isFollowing = async (practitionerId: string, followerId: string) => {
    const db = getFirestore(app);
    const followId = `${followerId}_${practitionerId}`;
    const followRef = doc(db, 'follows', followId);
    const followDoc = await getDoc(followRef);
    return followDoc.exists();
  };

  // Add a new function to get all following relationships for a user
  const getUserFollowing = async (userId: string) => {
    const db = getFirestore(app);
    const followsRef = collection(db, 'follows');
    const q = query(
      followsRef, 
      where('follower_id', '==', userId),
      orderBy('created_at', 'desc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => doc.data() as FollowRelation);
  };

  useEffect(() => {
    const fetchFirebaseData = async () => {
      try {
        const db = getFirestore(app);
        const events = collection(db, 'events');
        const providers = collection(db, 'providers');
        const blogs = collection(db, 'blogs');
        
        const [eventsSnapshot, providersSnapshot, blogsSnapshot] = await Promise.all([
          getDocs(events),
          getDocs(providers),
          getDocs(blogs)
        ]);

        const eventsData = eventsSnapshot.docs.map(doc => ({
          ...(doc.data() as EventData)
        }));
        const providerData = providersSnapshot.docs.map(doc => ({
          ...(doc.data() as ProviderData)
        }));

        // Combine the data
        const combinedData = eventsData.map(event => ({
          ...event,
          date: formatDate(event.date),
          providerDetails: providerData.find(
            provider => provider.provider_name === event.provider_name
          ) || null,
          // Ensure all required fields are present with default values if necessary
          event_name: event.event_name || '',
          event_description: event.event_description || '',
          start_time: event.start_time || '',
          event_link: event.event_link || '',
          event_types: event.event_types || [],
          end_time: event.end_time || '',
          address: event.address || '',
        }));

        const blogsData = blogsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...(doc.data() as BlogData)
        }));

        setData({
          eventsData: combinedData,
          providerData,
          blogsData,
          loading: false,
          error: null
        });
      } catch (err) {
        setData(prev => ({
          ...prev,
          loading: false,
          error: err as Error
        }));
      }
    };

    fetchFirebaseData();
  }, [app]);

  return (
    <DataContext.Provider 
      value={{
        ...data,
        createProviderWithProfile,
        followPractitioner,
        unfollowPractitioner,
        isFollowing,
        getUserFollowing
      }}
    >
      {children}
    </DataContext.Provider>
  );
};